import { ApolloClient } from "@apollo/client";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import {
  PURCHASER_GRAPHQL_MUTATION_OPTIONS,
  requirePurchaserGraphQLMutationPayload,
} from "./PurchaserRepositoryUtils";
import {
  CREATE_BRAND_PURCHASER_ASSOCIATION,
  CREATE_PURCHASER,
  GET_PURCHASER_BRANDS,
  CREATE_PURCHASER_PORTAL_KEY,
  GET_PURCHASER_CONFIG_TEMPLATE,
  GET_PURCHASER_PORTAL_KEYS,
  GET_PURCHASERS,
  GET_PURCHASERS_LOOKUP,
  GET_BRAND_ASSOCIATED_PURCHASERS_LOOKUP,
  UPDATE_PURCHASER,
  UPDATE_PURCHASER_CONFIG_TEMPLATE,
  UPDATE_PURCHASER_PORTAL_KEY,
} from "@src/domain/graphql/purchasers";
import {
  Purchaser as PurchaserEntity,
  PurchaserBrandAssociation,
  PurchaserConfigTemplate,
  PurchaserPortalKey,
  type IPurchaser,
  type IPurchaserConfigTemplate,
  type IPurchaserPortalKey,
  type Purchaser,
} from "@src/domain/entities/purchaser";
import type {
  CreateBrandPurchaserAssociationDTO,
  CreatePurchaserDTO,
  BrandAssociatedPurchasersLookupResponse,
  CreatePurchaserPortalKeyDTO,
  PurchaserLookupItem,
  PurchasersLookupResponse,
  UpdatePurchaserDTO,
  UpdatePurchaserConfigTemplateDTO,
  UpdatePurchaserPortalKeyDTO,
} from "@src/domain/dto/purchasers";
import type { IPurchaserRepository } from "@src/domain/repositories/purchaser";

type IPurchaserApi = IPurchaser & {
  brandPurchaser?: unknown;
  configTemplate?: IPurchaserConfigTemplate | null;
};

function mapPurchaser(item: IPurchaserApi): Purchaser {
  return PurchaserEntity.from({
    purchaserId: item.purchaserId,
    purchaserKey: item.purchaserKey,
    purchaserName: item.purchaserName,
    purchaserIsActive: item.purchaserIsActive,
    associatedBrandsCount: item.associatedBrandsCount ?? null,
    associatedBrands: (item.associatedBrands ?? []).map((brand) => ({
      brandId: brand.brandId,
      brandName: brand.brandName,
      brandKey: brand.brandKey,
      brandPurchaserId: brand.brandPurchaserId,
      purchaserInboxEmailId: brand.purchaserInboxEmailId,
      isActive: brand.isActive,
    })),
    configTemplate: item.configTemplate
      ? mapConfigTemplate(item.configTemplate)
      : null,
  });
}

function mapPortalKey(item: IPurchaserPortalKey): PurchaserPortalKey {
  return PurchaserPortalKey.from({
    id: item.id,
    portalKeyName: item.portalKeyName,
    portalKey: item.portalKey,
    appPurchaserId: item.appPurchaserId,
    purchaserKey: item.purchaserKey,
  });
}

function mapConfigTemplate(
  item: IPurchaserConfigTemplate,
): PurchaserConfigTemplate {
  return PurchaserConfigTemplate.from({
    configTemplateId: item.configTemplateId,
    appPurchaserId: item.appPurchaserId,
    metadata: item.metadata ?? null,
    searchKeywords: item.searchKeywords ?? null,
    intelligenceFilter: item.intelligenceFilter ?? null,
    deductionTypeInvoicePatternForDiscount:
      item.deductionTypeInvoicePatternForDiscount ?? null,
    additionalFieldMapping: item.additionalFieldMapping ?? null,
    discountTermAgreementDaysTolerance:
      item.discountTermAgreementDaysTolerance ?? null,
    isActive: item.isActive ?? true,
  });
}

export function buildPurchaserBrandsMap(
  items: Purchaser[],
): Record<number, PurchaserBrandAssociation[]> {
  return Object.fromEntries(
    items.map((purchaser) => [
      purchaser.purchaserId,
      purchaser.associatedBrands.map((brand) =>
        PurchaserBrandAssociation.from(brand),
      ),
    ]),
  );
}

/** Seed config-template cache from list items (null = loaded, no template). */
export function seedPurchaserConfigTemplateCache(
  items: Purchaser[],
  cache: Map<number, PurchaserConfigTemplate | null>,
): void {
  for (const purchaser of items) {
    cache.set(purchaser.purchaserId, purchaser.configTemplate);
  }
}

export class PurchaserRepository implements IPurchaserRepository {
  private readonly client: ApolloClient;

  constructor(client: ApolloClient = GraphQlAdapter.getInstance().getClient()) {
    this.client = client;
  }

  async getPurchasers(page: number, limit: number) {
    const { data } = await this.client.query<{
      purchasers: {
        items: IPurchaserApi[];
        total: number;
        page: number;
        limit: number;
        totalActiveCount: number | null;
      };
    }>({
      query: GET_PURCHASERS,
      variables: { page_number: page, page_size: limit },
      fetchPolicy: "network-only",
    });

    const response = data?.purchasers;
    if (!response) {
      throw new Error("Failed to fetch purchasers");
    }

    return {
      items: response.items.map(mapPurchaser),
      total: response.total,
      page: response.page,
      limit: response.limit,
      totalActiveCount: response.totalActiveCount ?? 0,
    };
  }

  async getPurchasersLookup(): Promise<{
    items: PurchaserLookupItem[];
    count: number;
  }> {
    const { data } = await this.client.query<PurchasersLookupResponse>({
      query: GET_PURCHASERS_LOOKUP,
      fetchPolicy: "network-only",
    });

    const response = data?.purchasersLookup;
    if (!response) {
      throw new Error("Failed to fetch purchasers lookup");
    }

    return {
      items: (response.items ?? []).map((item) => ({
        purchaserId: item.purchaserId,
        purchaserKey: item.purchaserKey,
        purchaserName: item.purchaserName,
        purchaserIsActive: item.purchaserIsActive,
        configTemplate: item.configTemplate
          ? mapConfigTemplate(item.configTemplate)
          : null,
      })),
      count: response.count ?? 0,
    };
  }

  async getBrandAssociatedPurchasersLookup(brandId: number): Promise<{
    items: PurchaserLookupItem[];
    count: number;
  }> {
    const { data } = await this.client.query<BrandAssociatedPurchasersLookupResponse>({
      query: GET_BRAND_ASSOCIATED_PURCHASERS_LOOKUP,
      variables: { brandId },
      fetchPolicy: "network-only",
    });

    const response = data?.brandAssociatedPurchasersLookup;
    if (!response) {
      throw new Error("Failed to fetch brand associated purchasers lookup");
    }

    return {
      items: (response.items ?? []).map((item) => ({
        purchaserId: item.purchaserId,
        purchaserKey: item.purchaserKey,
        purchaserName: item.purchaserName,
        purchaserIsActive: item.purchaserIsActive,
        configTemplate: item.configTemplate
          ? mapConfigTemplate(item.configTemplate)
          : null,
      })),
      count: response.count ?? 0,
    };
  }

  async getPurchaserBrands(
    purchaserId: number,
  ): Promise<PurchaserBrandAssociation[]> {
    const { data } = await this.client.query<{
      purchaserBrands: PurchaserBrandAssociation[];
    }>({
      query: GET_PURCHASER_BRANDS,
      variables: { purchaserId },
      fetchPolicy: "network-only",
    });

    return data?.purchaserBrands ?? [];
  }

  async createPurchaser(params: CreatePurchaserDTO): Promise<Purchaser> {
    const result = await this.client.mutate<{
      createPurchaser: IPurchaserApi;
    }>({
      mutation: CREATE_PURCHASER,
      variables: {
        params: {
          purchaserName: params.data.purchaserName,
          purchaserKey: params.data.purchaserKey,
          isActive: params.data.isActive,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    const payload = requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.createPurchaser,
      "Failed to create purchaser",
    );

    return mapPurchaser(payload);
  }

  async updatePurchaser(params: UpdatePurchaserDTO): Promise<Purchaser> {
    const result = await this.client.mutate<{
      updatePurchaser: IPurchaserApi;
    }>({
      mutation: UPDATE_PURCHASER,
      variables: {
        params: {
          purchaserId: params.data.purchaserId,
          purchaserName: params.data.purchaserName,
          isActive: params.data.isActive,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    const payload = requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.updatePurchaser,
      "Failed to update purchaser",
    );

    return mapPurchaser(payload);
  }

  async createBrandPurchaserAssociation(
    params: CreateBrandPurchaserAssociationDTO,
  ): Promise<PurchaserBrandAssociation> {
    const result = await this.client.mutate<{
      createBrandPurchaserAssociation: PurchaserBrandAssociation;
    }>({
      mutation: CREATE_BRAND_PURCHASER_ASSOCIATION,
      variables: {
        params: {
          purchaserId: params.data.purchaserId,
          brandId: params.data.brandId,
          isActive: params.data.isActive,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    return requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.createBrandPurchaserAssociation,
      "Failed to create brand purchaser association",
    );
  }

  async getPurchaserConfigTemplate(
    purchaserId: number,
  ): Promise<PurchaserConfigTemplate | null> {
    const { data, error } = await this.client.query<{
      purchaserConfigTemplate: IPurchaserConfigTemplate | null;
    }>({
      query: GET_PURCHASER_CONFIG_TEMPLATE,
      variables: { purchaserId },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    if (error instanceof Error && error.message.trim()) {
      throw error;
    }

    const payload = data?.purchaserConfigTemplate;
    if (payload == null) {
      return null;
    }

    return mapConfigTemplate(payload);
  }

  async updatePurchaserConfigTemplate(
    params: UpdatePurchaserConfigTemplateDTO,
  ): Promise<PurchaserConfigTemplate> {
    const result = await this.client.mutate<{
      updatePurchaserConfigTemplate: IPurchaserConfigTemplate;
    }>({
      mutation: UPDATE_PURCHASER_CONFIG_TEMPLATE,
      variables: {
        params: {
          purchaserId: params.data.purchaserId,
          metadata: params.data.metadata ?? null,
          searchKeywords: params.data.searchKeywords ?? null,
          intelligenceFilter: params.data.intelligenceFilter ?? null,
          deductionTypeInvoicePatternForDiscount:
            params.data.deductionTypeInvoicePatternForDiscount ?? null,
          additionalFieldMapping: params.data.additionalFieldMapping ?? null,
          discountTermAgreementDaysTolerance:
            params.data.discountTermAgreementDaysTolerance ?? null,
          isActive: params.data.isActive ?? true,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    const payload = requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.updatePurchaserConfigTemplate,
      "Failed to save config template",
    );

    return mapConfigTemplate(payload);
  }

  async getPurchaserPortalKeys(
    purchaserId: number,
  ): Promise<PurchaserPortalKey[]> {
    const { data, error } = await this.client.query<{
      purchaserPortalKeys: IPurchaserPortalKey[] | null;
    }>({
      query: GET_PURCHASER_PORTAL_KEYS,
      variables: { purchaserId },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    if (error instanceof Error && error.message.trim()) {
      throw error;
    }

    return (data?.purchaserPortalKeys ?? []).map((item) => mapPortalKey(item));
  }

  async createPurchaserPortalKey(
    params: CreatePurchaserPortalKeyDTO,
  ): Promise<PurchaserPortalKey> {
    const result = await this.client.mutate<{
      createPurchaserPortalKey: IPurchaserPortalKey;
    }>({
      mutation: CREATE_PURCHASER_PORTAL_KEY,
      variables: {
        params: {
          purchaserId: params.data.purchaserId,
          purchaserKey: params.data.purchaserKey,
          portalKeyName: params.data.portalKeyName,
          portalKey: params.data.portalKey,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    const payload = requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.createPurchaserPortalKey,
      "Failed to save portal key",
    );

    return mapPortalKey(payload);
  }

  async updatePurchaserPortalKey(
    params: UpdatePurchaserPortalKeyDTO,
  ): Promise<PurchaserPortalKey> {
    const result = await this.client.mutate<{
      updatePurchaserPortalKey: IPurchaserPortalKey;
    }>({
      mutation: UPDATE_PURCHASER_PORTAL_KEY,
      variables: {
        params: {
          id: params.data.id,
          purchaserId: params.data.purchaserId,
          purchaserKey: params.data.purchaserKey,
          portalKeyName: params.data.portalKeyName,
        },
      },
      ...PURCHASER_GRAPHQL_MUTATION_OPTIONS,
    });

    const payload = requirePurchaserGraphQLMutationPayload(
      result,
      result.data?.updatePurchaserPortalKey,
      "Failed to save portal key",
    );

    return mapPortalKey(payload);
  }
}
